import { Link, useLocation } from "@tanstack/react-router";
import {
  type Dispatch,
  type ReactElement,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from "react";
import { cn } from "@ndd/ui";
import type { AlwaysAiUser } from "../../../../shared/api-types";
import { landingPathForRole } from "@/lib/permissions";
import { foldsRail, showsCrumbTrail } from "@/lib/rail-sections";
import {
  railCollapsed,
  railToggleIntent,
  type RailToggleIntent,
} from "@/lib/siderail-state";
import { usePageVisit } from "@/lib/use-page-visit";
import { RD_HOME_FLAG, useFeatureEnabled } from "@/lib/use-features";
import { AlwaysRail } from "./always-rail";
import { AlwaysWordmark } from "./always-rail-parts";
import { BreadcrumbDisplay } from "./breadcrumb";
import { RailAccountBarButton } from "./rail-account";
import type { ShellLookSwitch } from "./shell-header";
import {
  RAIL_SHELL_GEOMETRY,
  SHELL_CRUMB_ROW,
  ShellFrameProvider,
  ShellGeometryProvider,
  ShellSqueezeProvider,
  useIsDesktop,
} from "./shell-layout";
import {
  SiderailDrawer,
  SiderailScrim,
  SiderailToggles,
  useSiderailCollapse,
  useSiderailDrawer,
} from "./siderail-frame";

// The single-rail Always AI shell (NO-754), behind `always-brand`. One rail
// for every role, its sections drawn from permissions (lib/rail-sections.ts),
// full height beside the canvas on desktop: 300px expanded or the 64px icon
// strip, the RD shell's widths, so the Today panel and the 1280px squeeze hold.
// Below 1024px the rail is the same overlay drawer under a 56px bar.
//
// The canvas is the page's scroll region, with no bar above it: the rail names
// the page. Only a Session gets a borderless crumb trail over it, by pathname,
// so nothing moves within a page. On an open Appointment or a SOAP Draft the
// rail folds to the strip for the visit.
// Pages read the geometry, the squeeze and the frame from shell-layout.tsx and
// never import this module.

export function AlwaysShell({
  user,
  lookSwitch,
  children,
}: {
  user: AlwaysAiUser;
  lookSwitch?: ShellLookSwitch;
  children: ReactNode;
}): ReactElement {
  const pathname = useLocation().pathname;
  const isDesktop = useIsDesktop();
  const drawer = useSiderailDrawer(pathname, isDesktop);
  const density = useRailDensity(user.id, pathname);
  const fixedTop = RAIL_SHELL_GEOMETRY.fixedTop;

  return (
    <ShellFrameProvider value="rail">
      <ShellGeometryProvider value={RAIL_SHELL_GEOMETRY}>
        <div className="flex h-screen flex-col bg-background">
          <RailMobileBar user={user} drawer={drawer} lookSwitch={lookSwitch} />
          <div className="flex min-h-0 flex-1">
            <SiderailDrawer
              drawerRef={drawer.drawerRef}
              open={drawer.open}
              isDesktop={isDesktop}
              collapsed={density.collapsed}
              fixedTop={fixedTop}
            >
              <AlwaysRail
                user={user}
                pathname={pathname}
                collapsed={density.collapsed}
                isDesktop={isDesktop}
                drawerOpen={drawer.open}
                onToggleCollapsed={density.toggle}
                lookSwitch={lookSwitch}
              />
            </SiderailDrawer>
            <SiderailScrim
              open={drawer.open}
              fixedTop={fixedTop}
              onDismiss={() => drawer.setOpen(false)}
            />
            <RailCanvas
              pathname={pathname}
              mainRef={drawer.mainRef}
              inert={!isDesktop && drawer.open}
              setSqueezed={density.setSqueezed}
            >
              {children}
            </RailCanvas>
          </div>
        </div>
      </ShellGeometryProvider>
    </ShellFrameProvider>
  );
}

/**
 * The rail's density: the stored choice off fold paths, folded for the visit
 * on them. An expand lasts for this visit only: any pathname change, even
 * back to the page it was made on, folds the working surface again.
 */
function useRailDensity(
  userId: string,
  pathname: string
): {
  collapsed: boolean;
  toggle: () => void;
  setSqueezed: Dispatch<SetStateAction<boolean>>;
} {
  const frame = useSiderailCollapse(userId);
  const folded = foldsRail(pathname);
  const [expanded, setExpanded] = usePageVisit<true>(pathname);
  const collapsed = railCollapsed({
    stored: frame.storedCollapsed,
    folded,
    expandedThisVisit: expanded === true,
    squeezed: frame.squeezed,
    isSqueezeWidth: frame.isSqueezeWidth,
  });
  const actions: Record<RailToggleIntent, () => void> = {
    "expand-visit": () => setExpanded(true),
    "collapse-visit": () => setExpanded(null),
    // Negate the rendered state, not the stored one, as the RD shell does.
    "store-collapsed": () => frame.setAndStoreCollapsed(true),
    "store-expanded": () => frame.setAndStoreCollapsed(false),
  };

  return {
    collapsed,
    toggle: () => actions[railToggleIntent({ folded, collapsed })](),
    setSqueezed: frame.setSqueezed,
  };
}

// Below lg only: the drawer toggle, the wordmark and the account menu's
// avatar (the same menu as the account row at the drawer's foot).
function RailMobileBar({
  user,
  drawer,
  lookSwitch,
}: {
  user: AlwaysAiUser;
  drawer: ReturnType<typeof useSiderailDrawer>;
  lookSwitch?: ShellLookSwitch;
}): ReactElement {
  const rdHomeEnabled = useFeatureEnabled(RD_HOME_FLAG);

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 lg:hidden">
      <SiderailToggles
        collapsed={false}
        drawerOpen={drawer.open}
        mobileToggleRef={drawer.mobileToggleRef}
        onToggleCollapsed={() => {}}
        onToggleDrawer={() => drawer.setOpen((value) => !value)}
      />
      <Link
        to={landingPathForRole(user, rdHomeEnabled)}
        aria-label="Always AI"
        className="text-foreground"
      >
        <AlwaysWordmark />
      </Link>
      <div className="ml-auto">
        <RailAccountBarButton user={user} lookSwitch={lookSwitch} />
      </div>
    </header>
  );
}

function RailCanvas({
  pathname,
  mainRef,
  inert,
  setSqueezed,
  children,
}: {
  pathname: string;
  mainRef: RefObject<HTMLElement | null>;
  inert: boolean;
  setSqueezed: Dispatch<SetStateAction<boolean>>;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {showsCrumbTrail(pathname) ? (
        <div className={cn(SHELL_CRUMB_ROW, "flex items-center overflow-hidden px-6")}>
          <BreadcrumbDisplay leadingSeparator={false} />
        </div>
      ) : null}
      {/* `relative` keeps absolutely positioned descendants (sr-only live
          regions) inside this scroll container, as in the RD shell. */}
      <main
        ref={mainRef}
        inert={inert}
        className="relative min-h-0 flex-1 overflow-y-auto"
      >
        <ShellSqueezeProvider value={setSqueezed}>{children}</ShellSqueezeProvider>
      </main>
    </div>
  );
}
